/* eslint-disable no-shadow */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import queryString from 'query-string';
import nprogress from 'nprogress';
import 'nprogress/nprogress.css';
import { Box, CircularProgress, useMediaQuery } from '@mui/material';
import apis from '@src/apis';

import Layout from '@src/containers/Layout';
import SwiperHeaderBanner from '@src/containers/Banner/SwiperHeaderBanner';
import actions from '@src/redux/actions';
import { HOME_PAGE_URL, LANDING_PAGE_URL, VBEE_DOMAIN } from '@src/configs';
import { setCookie } from '@src/utils/cookie';
import { ERROR_MESSAGE_AUTH, SERVICE_SELECT_TYPE } from '@src/constants';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import useLocation from '@src/hooks/useLocation';
import route from '@src/constants/route';
import LayoutMobileTTS from '@src/containers/Layout/LayoutMobileTTS';
import { checkUrlError } from '@src/utils/url';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import useUserInfo from '@src/hooks/useTtsUser';
import theme from '@src/styles/theme';
import { checkUserEligibility } from '@src/services/referral';
import useTopPaidEvent from '@src/hooks/useTopPaidEvent';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import { dubbingAppRoutes, ttsAppRoutes } from './newAppRoutes';
import appRoutes from './appRoutes';

const headerBannerReferral = [
  {
    id: 1,
    isReferralBanner: true,
  },
];

const PrivateApp = () => {
  const { user } = useSelector((state) => state.auth);
  const { headerVoucher } = useSelector((state) => state.voucher);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isUserEligibleForReferral = checkUserEligibility(user);
  const { config: eventConfig, isDetailPageOpen: isEventPageOpen } =
    useTopPaidEvent();
  // Mobile's only way in, so it stays until the draw like the desktop header button.
  const showEventHeaderBanner =
    isMobile && isEventPageOpen && eventConfig.bannerEnabled;
  const newHeaderBanner = useMemo(() => {
    const banners =
      isUserEligibleForReferral && isMobile
        ? [...headerBannerReferral, ...(headerVoucher || [])]
        : headerVoucher;

    if (isMobile) {
      return [
        ...(showEventHeaderBanner
          ? [{ id: 3, isTopPaidEventBanner: true }]
          : []),
        { id: 2, isOnboardingTasksBanner: true },
        ...(banners || []),
      ];
    }
    return banners;
  }, [
    isUserEligibleForReferral,
    headerVoucher,
    isMobile,
    headerBannerReferral,
    showEventHeaderBanner,
  ]);

  const newAppRoutes = [...ttsAppRoutes, ...dubbingAppRoutes];
  // Lọc bỏ route leaderboard ra khỏi privateRoutes
  const privateRoutes = newAppRoutes.filter(
    (r) => r.isPrivate && r.path !== route.VOICE_LEADERBOARD,
  );

  const isShowHeaderBanner = newHeaderBanner && newHeaderBanner.length > 0;

  return (
    <div>
      {isShowHeaderBanner && (
        <SwiperHeaderBanner
          headerVouchers={newHeaderBanner}
          isAgreeToTerm={user?.hasAgreedToTerms}
        />
      )}
      <Layout headerVoucher={newHeaderBanner}>
        <Switch>
          {privateRoutes.map((privateRoute) => (
            <PrivateRoute
              path={privateRoute.path}
              component={privateRoute.component}
              exact
              key={privateRoute.path}
              featureKey={privateRoute.featureKey}
            />
          ))}
          <Route path={route.MOBILE} component={LayoutMobileTTS} />
          <Route render={() => window.location.assign(HOME_PAGE_URL)} />
        </Switch>
      </Layout>
    </div>
  );
};

const AppRouter = () => {
  const [isFirstTime, setIsFirstTime] = useState(true);
  const {
    initialized,
    keycloak,
    getKcTokenFromCookie,
    setKcToken,
    validateKcTokenCookie,
  } = useCustomKeycloak();

  const { ip } = useLocation();
  const errorParams = new URLSearchParams(window.location.search).get('error');

  const { getFeatureValue } = useFeatureFlags();

  const { verifying } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const dispatch = useDispatch();

  if (!nprogress.isStarted()) nprogress.start();

  const SERVICE_SELECT_EXPIRED = 1000 * 60 * 60 * 24 * 14; // 14 days

  const validateTokenFromCookie = async (token, refreshToken, idToken) => {
    const isValidTokens = await validateKcTokenCookie(
      token,
      refreshToken,
      idToken,
    );
    if (isValidTokens) {
      setKcToken(token, refreshToken, idToken);
      dispatch(actions.auth.verifyToken(token));
    }
    setIsFirstTime(false);
  };

  const redirectToUrlInLoginCode = async (loginCode) => {
    const res = await apis.loginCode.verifyLoginCode(loginCode);
    if (!res.status) return;

    const { redirectUrl } = res.result || {};
    if (!redirectUrl) return;
    window.location.assign(redirectUrl);
  };

  useEffect(() => {
    nprogress.done();
  });

  useEffect(() => {
    dispatch(actions.language.getLanguages());
    dispatch(actions.voice.getDubbingVoices());
  }, []);

  useEffect(() => {
    if (!initialized) return;
    const params = queryString.parse(window.location.search);
    const { loginCode } = params;

    if (keycloak.token) {
      dispatch(actions.auth.verifyToken(keycloak.token));
      const { sub: userId, email, phoneNumber } = keycloak.tokenParsed || {};
      const userInFeatureVoiceCloning = getFeatureValue(
        FEATURE_KEYS.VOICE_CLONING,
        {
          ip,
          userId,
          email,
          phoneNumber,
        },
      );
      const loginByQR = validateUserPackageCanUseVoiceCloning({
        ttsUser,
        isUserInWhiteList: userInFeatureVoiceCloning,
      });
      if (loginCode && loginByQR) redirectToUrlInLoginCode(loginCode);
    } else {
      const { token, refreshToken, idToken } = getKcTokenFromCookie();
      if (token) {
        validateTokenFromCookie(token, refreshToken, idToken);
        return;
      }
    }
    setIsFirstTime(false);
  }, [initialized, keycloak.token, keycloak?.authenticated]);

  useEffect(() => {
    if (initialized && keycloak?.authenticated) {
      const path = window.location.pathname;
      const product = path.split('/')[1];
      const userId = keycloak?.idTokenParsed?.sub;
      setCookie({
        cname: `ServiceSelect-${userId}`,
        cvalue:
          product === 'dubbing'
            ? SERVICE_SELECT_TYPE.DUBBING
            : SERVICE_SELECT_TYPE.STUDIO,
        domain: VBEE_DOMAIN,
        extime: SERVICE_SELECT_EXPIRED,
      });
    }
  }, [initialized, keycloak?.authenticated]);

  // Check page is error page
  const isUrlError = checkUrlError();

  // Check error is auth error
  const authError = ERROR_MESSAGE_AUTH[errorParams];

  // Redirect to error page if error is auth error
  if (authError && !isUrlError) {
    window.location.assign(`/error?error=${authError.message}`);
  }

  const shouldShowLoader = !initialized || verifying || isFirstTime;

  if (shouldShowLoader && !authError) {
    return (
      <Box
        height="100vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }
  const publicRoutes = appRoutes.filter((route) => !route.isPrivate);

  // Lấy route leaderboard từ leaderboardAppRoutes
  const leaderboardRoute = ttsAppRoutes.find(
    (r) => r.path === route.VOICE_LEADERBOARD,
  );

  return (
    <BrowserRouter>
      <Switch>
        <Route
          exact
          path="/"
          render={() => window.location.assign(LANDING_PAGE_URL)}
        />
        {publicRoutes.map((publicRoute) => (
          <PublicRoute
            exact
            path={publicRoute.path}
            component={publicRoute.component}
            restricted={publicRoute.restricted}
            key={publicRoute.path}
          />
        ))}

        {/* Route leaderboard không bọc Layout */}
        {leaderboardRoute && (
          <PrivateRoute
            exact
            path={leaderboardRoute.path}
            component={leaderboardRoute.component}
            restricted={leaderboardRoute.restricted}
            key={leaderboardRoute.path}
            featureKey={leaderboardRoute.featureKey}
          />
        )}

        <PrivateRoute component={PrivateApp} />
      </Switch>
    </BrowserRouter>
  );
};

export default AppRouter;
